import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Typography,
  useTheme
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import InputField from 'src/common/formFields/InputField';
import SelectField from 'src/common/formFields/SelectField';
import { SwitchInput } from 'src/common/FormElements';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { getAllSites } from 'src/redux/siteSlice';
import { addTank, updateTank } from 'src/redux/tankSlice';
import {
  tankFormInitialValues,
  tankFormModel,
  tankValidationSchema
} from './tankFormModel';

const SENSOR_INWARD_PROVIDERS = ['OMNICOMM', 'OMINCOMM'];

const supportsSensorInward = (provider) => (
  SENSOR_INWARD_PROVIDERS.includes(String(provider || '').trim().toUpperCase())
);

const NewTank = ({ open, handleClose, initialValues }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { allSites } = useSelector((state) => state.sites);
  const requiredHintText = '';

  const formInitValues = {
    ...tankFormInitialValues,
    ...(initialValues || {}),
    // Existing tanks may return null for optional fleet-provider fields.
    // Formik/Yup expects these string fields to use an empty string instead.
    fleet_service_provider: initialValues?.fleet_service_provider ?? '',
    fleet_sensor_name: initialValues?.fleet_sensor_name ?? '',
  };
  const isEditMode = Boolean(initialValues);

  const {
    formId,
    formField: {
      siteName,
      tankType,
      article,
      tankName,
      tankCode,
      sensorId,
      maxVolume,
      minVolume,
      maxLevel,
      minLevel,
      automation,
      fuelLevelOnly,
      fleetProviderEnabled,
      fleetServiceProvider,
      fleetSensorName,
      sensorInwardEnabled
    }
  } = tankFormModel;

  useEffect(() => {
    if (open) {
      dispatch(getAllSites());
    }
  }, [open, dispatch]);

  const onSubmit = async (values, actions) => {
    const payload = { ...values };
    if (!payload.fleet_provider_enabled) {
      payload.fleet_service_provider = '';
      payload.fleet_sensor_name = '';
      payload.sensor_inward_enabled = false;
    } else if (!supportsSensorInward(payload.fleet_service_provider)) {
      payload.sensor_inward_enabled = false;
    }

    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(updateTank(payload)).unwrap();
      } else {
        await dispatch(addTank(payload)).unwrap();
      }

      handleClose();

      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Tank updated successfully'
            : 'New tank created successfully'
        })
      );
    } catch (error) {
      actions.setSubmitting(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message || 'Something went wrong' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FMSModal
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      paperSx={{ display: 'flex', flexDirection: 'column' }}
    >
      <FMSModalTitle
        title={initialValues ? 'EDIT TANK' : 'ADD NEW TANK'}
        onCancel={handleClose}
      />

      <Divider />

      <Formik
        initialValues={formInitValues}
        validationSchema={tankValidationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values }) => (
          <>
            {loading ? (
              <Loader />
            ) : (
              <Form
                id={formId}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden'
                }}
              >
                <DialogContent sx={{
                  p: 3,
                  flex: 1,
                  minHeight: 0,
                  maxHeight: 'none',
                  overflowY: 'auto'
                }}
                >
                  {!isEditMode && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      * Required fields
                    </Typography>
                  )}
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={siteName.name}
                        label={`${siteName.label} *`}
                        data={allSites}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={tankType.name}
                        label={`${tankType.label} *`}
                        data={tankType.data}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={tankName.name}
                        label={`${tankName.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={tankCode.name}
                        label={`${tankCode.label} *`}
                        upperCase
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={article.name}
                        label={`${article.label} *`}
                        data={article.data}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={sensorId.name}
                        label={`${sensorId.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={maxVolume.name}
                        label={`${maxVolume.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={minVolume.name}
                        label={`${minVolume.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={maxLevel.name}
                        label={`${maxLevel.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={minLevel.name}
                        label={`${minLevel.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SwitchInput
                        name={automation.name}
                        label={automation.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SwitchInput
                        name={fuelLevelOnly.name}
                        label={fuelLevelOnly.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SwitchInput
                        name={fleetProviderEnabled.name}
                        label={fleetProviderEnabled.label}
                        fullWidth
                      />
                    </Grid>

                    {values[fleetProviderEnabled.name] && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <SelectField
                            name={fleetServiceProvider.name}
                            label={`${fleetServiceProvider.label} *`}
                            data={fleetServiceProvider.data}
                            required
                            requiredHintText={requiredHintText}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <InputField
                            name={fleetSensorName.name}
                            label={`${fleetSensorName.label} *`}
                            required
                            requiredHintText={requiredHintText}
                            fullWidth
                          />
                        </Grid>

                        {supportsSensorInward(values[fleetServiceProvider.name]) && (
                          <Grid item xs={12} sm={6}>
                            <SwitchInput
                              name={sensorInwardEnabled.name}
                              label={sensorInwardEnabled.label}
                              fullWidth
                            />
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </DialogContent>

                <DialogActions
                  sx={{
                    p: 2.5,
                    flexShrink: 0,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: '#F9FAFB'
                  }}
                >
                  <Button
                    onClick={handleClose}
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3
                    }}
                  >
                    {initialValues ? 'Update Tank' : 'Create Tank'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </>
        )}
      </Formik>
    </FMSModal>
  );
};

NewTank.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewTank;
